import { useState, useEffect, useRef, useCallback } from 'react'
import PartyRevealScreen from './PartyRevealScreen'
import { IconClapper, IconCheck, IconClock } from '../icons'

// Readiness barrier: after the LCA animation the host waits up to this long for
// every confirmed player to ack lcaReady before starting gameplay. The timeout
// fallback means a dead/slow phone can't freeze the party (laggards reconcile via
// the status change once they recover).
const LCA_BARRIER_TIMEOUT_MS = 9000

/** True when every confirmed (ready) player has acked lcaReady. Empty/none → true. */
function allConfirmedPlayersReady(party) {
  const confirmed = Object.values(party?.players ?? {}).filter((p) => p?.ready === true)
  if (confirmed.length === 0) return true
  return confirmed.every((p) => p?.lcaReady === true)
}

/**
 * Host/TV screen during the actor-picking and reveal phases.
 *
 * Status flow this component handles:
 *   actor_pick → reveal → countdown → (go — fires onCountdownDone)
 *
 * Blind picking: pickers' picks are stored in party.pick0 / party.pick1.
 * When both are in, the last picker's device assigns start/goal and sets
 * status:'reveal'. The host shows lock-in progress, then the reveal, then LCA.
 */
export default function PartyActorPick({ party, onCountdownStart, onCountdownDone }) {
  const [lcaPhase, setLcaPhase] = useState(null) // null | 'lights' | 'camera' | 'action' | 'barrier' | 'go'
  const revealAdvancedRef = useRef(false)
  const barrierFiredRef = useRef(false)

  const status = party?.status
  const isReveal   = status === 'reveal'
  const isCountdown = status === 'countdown'

  // When status becomes 'reveal', auto-advance to 'countdown' after 5 s (host only).
  // This gives everyone time to absorb the matchup before LCA fires.
  useEffect(() => {
    if (!isReveal || revealAdvancedRef.current) return
    revealAdvancedRef.current = true
    const id = setTimeout(() => onCountdownStart?.(), 5000)
    return () => clearTimeout(id)
  }, [isReveal]) // eslint-disable-line react-hooks/exhaustive-deps

  // Start LCA sequence when Firestore status becomes 'countdown'
  useEffect(() => {
    if (isCountdown && lcaPhase === null) setLcaPhase('lights')
  }, [isCountdown, lcaPhase])

  // Sequence the LCA phases; the 'action' beat hands off to the readiness barrier
  // (below) rather than starting gameplay directly.
  useEffect(() => {
    if (lcaPhase === 'lights') {
      const id = setTimeout(() => setLcaPhase('camera'), 1200)
      return () => clearTimeout(id)
    }
    if (lcaPhase === 'camera') {
      const id = setTimeout(() => setLcaPhase('action'), 1200)
      return () => clearTimeout(id)
    }
    if (lcaPhase === 'action') {
      const id = setTimeout(() => setLcaPhase('barrier'), 1400)
      return () => clearTimeout(id)
    }
  }, [lcaPhase]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Readiness barrier ───────────────────────────────────────────────────────
  // Fire the single gameplay-status write (onCountdownDone) exactly once, when
  // every confirmed player has acked OR the timeout elapses — whichever first.
  const fireCountdownDone = useCallback(() => {
    if (barrierFiredRef.current) return
    barrierFiredRef.current = true
    setLcaPhase('go')
    onCountdownDone?.()
  }, [onCountdownDone])

  // Timeout fallback — armed once when the barrier opens.
  useEffect(() => {
    if (lcaPhase !== 'barrier') return
    const id = setTimeout(fireCountdownDone, LCA_BARRIER_TIMEOUT_MS)
    return () => clearTimeout(id)
  }, [lcaPhase, fireCountdownDone])

  // All-ready check — re-runs on every party snapshot while the barrier is open.
  useEffect(() => {
    if (lcaPhase !== 'barrier') return
    if (allConfirmedPlayersReady(party)) fireCountdownDone()
  }, [lcaPhase, party, fireCountdownDone])

  // New round / re-entry: reset the LCA + barrier state so it can run again next
  // round (the refs otherwise latch and would block a second countdown).
  useEffect(() => {
    if (status === 'actor_pick') {
      setLcaPhase(null)
      revealAdvancedRef.current = false
      barrierFiredRef.current = false
    }
  }, [status])

  if (!party) {
    return (
      <div className="party-actor-pick">
        <p style={{ color: 'var(--text-muted)' }}>Loading...</p>
      </div>
    )
  }

  // ── LCA sequence ──────────────────────────────────────────────────────────
  if (lcaPhase && lcaPhase !== 'go') {
    // 'barrier' holds the fully-revealed "Action!" frame while we wait for players.
    const showLights = ['lights', 'camera', 'action', 'barrier'].includes(lcaPhase)
    const showCamera = ['camera', 'action', 'barrier'].includes(lcaPhase)
    const showAction = ['action', 'barrier'].includes(lcaPhase)
    return (
      <div className="party-lca-screen">
        <div className="party-lca-screen__words">
          <span className={`party-lca-word${showLights ? ' party-lca-word--visible' : ''}`}>
            Lights...
          </span>
          <span className={`party-lca-word${showCamera ? ' party-lca-word--visible' : ''}`}>
            Camera...
          </span>
          <span className={`party-lca-word party-lca-word--action${showAction ? ' party-lca-word--visible' : ''}`}>
            Action! <IconClapper size={24} style={{ verticalAlign: 'middle' }} />
          </span>
        </div>
      </div>
    )
  }

  // ── Reveal screen ─────────────────────────────────────────────────────────
  if (isReveal) {
    return (
      <PartyRevealScreen
        startActor={party.startActor}
        goalActor={party.goalActor}
      />
    )
  }

  // ── Picking in progress (status === 'actor_pick') ─────────────────────────
  const pickers = party.actorPickerIds ?? []
  const players  = party.players ?? {}
  const pick0Done = !!party.pick0
  const pick1Done = !!party.pick1

  return (
    <div className="party-actor-pick">
      <p className="party-actor-pick__prompt">The cast is being chosen...</p>
      <div className="party-actor-pick__pickers">
        {pickers.map((uid, i) => {
          const player = players[uid]
          const done   = i === 0 ? pick0Done : pick1Done
          return (
            <div key={uid} className="party-actor-pick__picker-chip">
              <div className="party-actor-pick__picker-avatar">
                {player?.avatar
                  ? <img src={player.avatar} alt={player.username} />
                  : <IconClapper size={20} />}
              </div>
              <span className="party-actor-pick__picker-name">
                {player?.username || 'Guest'}
              </span>
              <span className={`party-actor-pick__picker-status${done ? ' party-actor-pick__picker-status--done' : ''}`}>
                {done
                  ? <><IconCheck size={13} style={{ verticalAlign: 'middle' }} /> Locked in</>
                  : <><IconClock size={13} style={{ verticalAlign: 'middle' }} /> Choosing...</>}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
